import { Transition } from "@headlessui/react";
import { bisector } from "d3-array";
import { ScaleBand, scaleBand, ScaleLinear, scaleLinear } from "d3-scale";
import { arc, area, line, pie, PieArcDatum } from "d3-shape";
import { identity } from "lodash";
import { Fragment, useState } from "react";
import { TMessage, TMessageParam } from "../i18n/tt";
import { useI18n } from "../stores/RootStoreContext";
import { classNames } from "../utils/classNames";
import { immediate, when } from "../utils/functions";
import { PreppedI18nState } from "../i18n/i18nDomain";
import { extent } from "./sparklineHelpers";

const scaleVal = <T,>(s: PreppedSparklineScale<T>, d: T, i: number, ds: T[]): number => {
  switch (s.scaleType) {
    case "linear":
      return s.scale(s.val(d, i, ds));
    case "band":
      return s.scale(s.val(d, i, ds)) ?? 0;
  }
};

const scaleCenter = <T,>(s: PreppedSparklineScale<T>, d: T, i: number, ds: T[]): number => {
  switch (s.scaleType) {
    case "linear":
      return s.scale(s.val(d, i, ds));
    case "band":
      return (s.scale(s.val(d, i, ds)) ?? 0) + s.scale.bandwidth() / 2;
  }
};

export type SparklineLineProps = {
  type: "line";
  area?: boolean;
};

export const SparklineLine = <T,>(props: SparklineLineProps & SparklineContext<T>) => {
  const x = props.x;
  const y = props.y;
  const offset = props.x.scaleType === "band" ? props.x.scale.bandwidth() / 2 : 0;
  const l = line<T>()
    .x((d, i, ds) => scaleVal(x, d, i, ds) + offset)
    .y((d, i, ds) => scaleVal(y, d, i, ds));
  const a =
    props.area &&
    area<T>()
      .x((d, i, ds) => scaleVal(x, d, i, ds))
      .y0(
        immediate(() => {
          switch (y.scaleType) {
            case "linear":
              return y.scale(y.domain[0]);
            case "band":
              return 0;
          }
        }),
      )
      .y1((d, i, ds) => scaleVal(y, d, i, ds));
  return (
    <>
      <path fill={"none"} stroke={"currentColor"} strokeWidth={2} d={String(l(props.data))} />
      {a && <path fill={"currentColor"} fillOpacity={0.2} d={String(a(props.data))} />}
    </>
  );
};

export type SparklineDotsProps = {
  type: "dots";
};

export const SparklineDots = <T,>(props: SparklineDotsProps & SparklineContext<T>) => {
  const x = props.x;
  const y = props.y;
  const offset = props.x.scaleType === "band" ? props.x.scale.bandwidth() / 2 : 0;
  return (
    <>
      {props.data.map((d, i, ds) => (
        <circle
          key={`dot-${i}`}
          cx={scaleVal(x, d, i, ds) + offset}
          cy={scaleVal(y, d, i, ds)}
          r={3}
          fill={"currentColor"}
        />
      ))}
    </>
  );
};

export type SparklineBarsProps = {
  type: "bars";
};

export const SparklineBars = <T,>({
  height,
  data,
  x,
  y,
}: SparklineBarsProps & SparklineContext<T>) => {
  if (x.scaleType !== "band") {
    return null;
  }
  return (
    <>
      {data.map((d, i, ds) => (
        <Fragment key={x.val(d, i, ds)}>
          <rect
            x={scaleVal(x, d, i, ds)}
            y={scaleVal(y, d, i, ds)}
            width={x.scale.bandwidth()}
            height={height - scaleVal(y, d, i, ds)}
            fill={"currentColor"}
            fillOpacity={0.2}
          />
          <rect
            x={scaleVal(x, d, i, ds)}
            y={scaleVal(y, d, i, ds)}
            width={x.scale.bandwidth()}
            height={2}
            fill={"currentColor"}
          />
        </Fragment>
      ))}
    </>
  );
};

export type SparklineXAxisProps = {
  type: "x-axis";
};

export const SparklineXAxis = <T,>({
  data,
  x,
  height,
}: SparklineXAxisProps & SparklineContext<T>) => {
  const bandwidth = x.scaleType === "band" ? x.scale.bandwidth() : 0;
  const padding = x.scaleType === "band" ? x.scale.padding() : 0;
  const width = Math.floor(bandwidth + bandwidth * padding * 2);
  return (
    <div
      className={classNames(
        "absolute",
        "tw-top-0",
        "tw-left-0",
        "color-default",
        "text-center",
        bandwidth && bandwidth < 50 ? "text-xs" : "text-sm",
      )}
    >
      {data.map((d, i, ds) => (
        <div
          key={i}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            transform: `translate(${scaleCenter(x, d, i, ds) - width / 2}px, calc(${height}px + 1em))`,
            width: width,
          }}
          data-tooltip={x.formatTickVal ? x.formatVal(d, i, ds) : undefined}
        >
          {x.formatTickVal ? x.formatTickVal(d, i, ds) : x.formatVal(d, i, ds)}
        </div>
      ))}
    </div>
  );
};

export type SparklineHoverValueProps = { type: "hover-value" };

export const SparklineHoverValue = <T,>({
  margin,
  hoverData,
}: SparklineHoverValueProps & SparklineContext<T>) => {
  return (
    <Transition
      show={hoverData !== undefined}
      enter={"tw-transition-opacity"}
      enterFrom={"tw-opacity-0"}
      enterTo={"tw-opacity-100"}
      leave={"tw-transition-opacity"}
      leaveFrom={"tw-opacity-100"}
      leaveTo={"tw-opacity-0"}
    >
      <div
        className={classNames(
          "absolute",
          "color-default",
          "tw-pointer-events-none",
          "tw-left-0",
          "tw-top-0",
          "tw-transition-all",
        )}
        style={{
          transform: [
            `translate(${margin.left + (hoverData?.x ?? 0)}px,${margin.top + (hoverData?.y ?? 0)}px)`,
            "translate(-50%, -100%)",
          ].join(" "),
        }}
      >
        {hoverData?.formattedVal}
      </div>
    </Transition>
  );
};

export type SparklineValueProps = { type: "value" };

export const SparklineValue = <T,>({
  margin,
  data,
  x,
  y,
}: SparklineValueProps & SparklineContext<T>) => {
  return (
    <>
      {data.map((d, i, ds) => (
        <div
          key={i}
          className={"absolute tw-pointer-events-none tw-left-0 tw-top-0 color-default text-xs"}
          style={{
            transform: [
              `translate(${margin.left + (scaleCenter(x, d, i, ds) ?? 0)}px,${margin.top + (scaleVal(y, d, i, ds) ?? 0)}px)`,
              "translate(-50%, -100%)",
            ].join(" "),
          }}
        >
          {y.formatVal(d, i, ds)}
        </div>
      ))}
    </>
  );
};

export type SparklineComponent =
  | SparklineLineProps
  | SparklineBarsProps
  | SparklineDotsProps
  | SparklineXAxisProps
  | SparklineHoverValueProps
  | SparklineValueProps;

export type SparklineScale<T> =
  | {
      scaleType: "linear";
      val: (d: T, i: number, ds: T[]) => number;
      formatVal?: (d: T, i: number, ds: T[]) => string | TMessage | TMessageParam;
      formatTickVal?: (d: T, i: number, ds: T[]) => string | TMessage | TMessageParam;
      domain?: [number, number];
    }
  | {
      scaleType: "band";
      val: (d: T, i: number, ds: T[]) => string;
      formatVal?: (d: T, i: number, ds: T[]) => string | TMessage | TMessageParam;
      formatTickVal?: (d: T, i: number, ds: T[]) => string | TMessage | TMessageParam;
    };

export type PreppedSparklineScale<T> =
  | {
      scaleType: "linear";
      val: (d: T, i: number, ds: T[]) => number;
      formatVal: (d: T, i: number, ds: T[]) => string;
      formatTickVal?: (d: T, i: number, ds: T[]) => string;
      domain: [number, number];
      scale: ScaleLinear<number, number>;
      invert: (v: number) => number;
    }
  | {
      scaleType: "band";
      val: (d: T, i: number, ds: T[]) => string;
      formatVal: (d: T, i: number, ds: T[]) => string;
      formatTickVal?: (d: T, i: number, ds: T[]) => string;
      domain: string[];
      scale: ScaleBand<string>;
      invert: (v: number) => string;
    };

export type SparklineProps<T> = {
  data: T[];
  x: SparklineScale<T>;
  y: SparklineScale<T>;
  components: SparklineComponent[];
};

export type SparklineContext<T> = {
  margin: { top: number; left: number; bottom: number; right: number };
  width: number;
  height: number;
  data: T[];
  x: PreppedSparklineScale<T>;
  y: PreppedSparklineScale<T>;
  axisHeight: number;
  hoverData?: {
    formattedVal: string;
    x: number;
    y: number;
  };
};

const makeFormat =
  <T,>(
    f: (d: T, i: number, ds: T[]) => number | string | TMessage | TMessageParam,
    i18n: PreppedI18nState,
  ): PreppedSparklineScale<T>["formatVal"] =>
  (...args) => {
    const val = f(...args);
    switch (typeof val) {
      case "number":
        return String(val);
      case "string":
        return val;
      default:
        return i18n.tf(val);
    }
  };

const prepScale = <T,>(
  data: T[],
  size: number,
  orientation: "horizontal" | "vertical",
  s: SparklineScale<T>,
  i18n: PreppedI18nState,
): PreppedSparklineScale<T> => {
  const range = orientation === "horizontal" ? [0, size] : [size, 0];
  switch (s.scaleType) {
    case "linear": {
      const domain = s.domain ?? extent(data, s.val);
      const scale = scaleLinear(domain, range);
      return {
        ...s,
        formatVal: makeFormat(s.formatVal ?? s.val, i18n),
        formatTickVal: s.formatTickVal && makeFormat(s.formatTickVal, i18n),
        domain,
        scale,
        invert: (v) => scale.invert(v),
      };
    }
    case "band": {
      const domain = data.map(s.val);
      const scale = scaleBand().domain(domain).range(range).padding(0.5);
      return {
        ...s,
        formatVal: makeFormat(s.formatVal ?? s.val, i18n),
        formatTickVal: s.formatTickVal && makeFormat(s.formatTickVal, i18n),
        domain,
        scale,
        invert: (v) => {
          const paddingOuter = scale(domain[0]) ?? 0;
          const index = Math.floor((v - paddingOuter) / scale.step());
          return domain[index];
        },
      };
    }
  }
};

const prepXScale = <T,>(
  data: T[],
  width: number,
  x: SparklineScale<T>,
  i18n: PreppedI18nState,
): PreppedSparklineScale<T> => prepScale(data, width, "horizontal", x, i18n);

const prepYScale = <T,>(
  data: T[],
  height: number,
  y: SparklineScale<T>,
  i18n: PreppedI18nState,
): PreppedSparklineScale<T> => prepScale(data, height, "vertical", y, i18n);

export const Sparkline = <T,>(props: SparklineProps<T> & { width: number; height: number }) => {
  const i18n = useI18n();
  const [hoveredDataIndex, setHoveredDataIndex] = useState<number>(-1);
  const xAxisComponent = props.components.find((c) => c.type === "x-axis");
  const valueComponent = props.components.find((c) => c.type === "value");
  const hoverValueComponent = props.components.find((c) => c.type === "hover-value");
  const axisHeight = xAxisComponent ? 16 : 0;
  const margin = { top: 8, left: 3, bottom: 8, right: 3 };
  const innerWidth = props.width - margin.left - margin.right;
  const innerHeight = props.height - margin.top - margin.bottom - axisHeight;
  const x = prepXScale(props.data, innerWidth, props.x, i18n);
  const y = prepYScale(props.data, innerHeight, props.y, i18n);
  const context: SparklineContext<T> = {
    axisHeight,
    margin,
    data: props.data,
    width: innerWidth,
    height: innerHeight,
    x: x,
    y: y,
    hoverData: when(hoveredDataIndex >= 0, () => {
      const d = props.data[hoveredDataIndex];
      return {
        formattedVal: y.formatVal(d, hoveredDataIndex, props.data),
        x: scaleCenter(x, d, hoveredDataIndex, props.data),
        y: scaleVal(y, d, hoveredDataIndex, props.data),
      };
    }),
  };
  return (
    <div className={classNames("relative", "tw-overflow-visible")}>
      <svg
        width={props.width}
        height={props.height}
        pointerEvents={"all"}
        onMouseMove={(e) => {
          const svg = e.currentTarget as SVGGraphicsElement;
          const pt = new DOMPoint(e.clientX, e.clientY);
          const loc = pt.matrixTransform(svg.getScreenCTM()!.inverse());
          if (context.x.scaleType === "linear") {
            const xVals = context.data.map(context.x.val);
            const b = bisector<number, number>(identity);
            const index = b.center(xVals, context.x.invert(loc.x));
            setHoveredDataIndex(index);
          } else {
            const v = context.x.invert(loc.x);
            setHoveredDataIndex(
              context.data.findIndex((d, i, ds) => context.x.val(d, i, ds) === v),
            );
          }
        }}
        onMouseLeave={() => setHoveredDataIndex(-1)}
      >
        <g transform={"translate(" + margin.left + "," + margin.top + ")"}>
          {props.components.map((c, i) => {
            switch (c.type) {
              case "line":
                return <SparklineLine key={i} {...c} {...context} />;
              case "bars":
                return <SparklineBars key={i} {...c} {...context} />;
              case "dots":
                return <SparklineDots key={i} {...c} {...context} />;
            }
          })}
        </g>
      </svg>
      {xAxisComponent && <SparklineXAxis {...xAxisComponent} {...context} />}
      {valueComponent && <SparklineValue {...valueComponent} {...context} />}
      {hoverValueComponent && <SparklineHoverValue {...hoverValueComponent} {...context} />}
    </div>
  );
};

export type PieChartDatum = { value: number; label: string; isTotal?: boolean };

export type PieChartProps = {
  data: PieChartDatum[];
};

export const PieChart = ({
  width,
  height,
  data,
}: PieChartProps & { width: number; height: number }) => {
  const p = pie<PieChartDatum>().value((d) => d.value);
  const a = arc<PieArcDatum<PieChartDatum>>()
    .innerRadius(0)
    .outerRadius(Math.min(width, height) / 2);
  const arcs = p(data);
  const opacity = scaleLinear()
    .domain(
      extent(
        data.filter((d) => !d.isTotal),
        (d) => d.value,
      ),
    )
    .range([20, 100]);

  return (
    <svg
      width={width}
      height={height}
      viewBox={[-width / 2, -height / 2, width, height].join()}
      pointerEvents={"all"}
    >
      <g stroke={"white"}>
        {arcs.map((d) => (
          <path
            key={d.index}
            fill={"currentColor"}
            d={a(d) ?? ""}
            opacity={d.data.isTotal ? 0.2 : opacity(d.value) / 100}
          />
        ))}
      </g>
    </svg>
  );
};
